/**
 * Enhanced Performance Monitoring Utility
 * Following the New Relic blog approach for micro-frontend performance tracking
 */

class PerformanceMonitor {
  constructor() {
    this.activeMarks = new Map();
    this.newRelicAgent = null;
  }

  /**
   * Set New Relic agent reference for custom metrics
   * @param {Object} agent - New Relic agent instance
   */
  setNewRelicAgent(agent) {
    this.newRelicAgent = agent;
  }

  /**
   * Create a performance mark
   * @param {string} name - Name of the mark
   */
  mark(name) {
    try {
      if (performance && performance.mark) {
        performance.mark(name);
        console.log(`Performance mark created: ${name}`);
        this.activeMarks.set(name, performance.now());
        return true;
      }
    } catch (error) {
      console.warn(`Failed to create performance mark ${name}:`, error);
    }
    return false;
  }

  /**
   * Create a performance measure between two marks
   * @param {string} measureName - Name of the measure
   * @param {string} startMark - Start mark name
   * @param {string} endMark - End mark name
   */
  measure(measureName, startMark, endMark) {
    try {
      if (performance && performance.measure) {
        performance.measure(measureName, startMark, endMark);
        
        // Get the measure entry
        const measureEntries = performance.getEntriesByName(measureName, 'measure');
        if (measureEntries.length > 0) {
          const duration = measureEntries[measureEntries.length - 1].duration;
          console.log(`Performance measure created: ${measureName} - ${duration.toFixed(2)}ms`);
          
          // Send custom metric to New Relic
          this.sendCustomMetric(measureName, duration);
          
          return duration;
        }
      }
    } catch (error) {
      console.warn(`Failed to create performance measure ${measureName}:`, error);
    }
    return null;
  }

  /**
   * Send custom metric to New Relic with enhanced data
   * @param {string} metricName - Name of the metric
   * @param {number} value - Metric value
   * @param {Object} attributes - Additional attributes
   */
  sendCustomMetric(metricName, value, attributes = {}) {
    try {
      // Enhanced attributes for better filtering
      const enhancedAttributes = {
        ...attributes,
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        connectionType: navigator.connection?.effectiveType || 'unknown',
        memoryUsage: performance.memory ? {
          used: performance.memory.usedJSHeapSize,
          total: performance.memory.totalJSHeapSize,
          limit: performance.memory.jsHeapSizeLimit
        } : null
      };

      // Send as page action for detailed tracking
      if (this.newRelicAgent && this.newRelicAgent.addPageAction) {
        this.newRelicAgent.addPageAction('CustomMetric', {
          metricName: metricName,
          value: value,
          ...enhancedAttributes
        });
      }
      
      // Send as custom event
      if (this.newRelicAgent && this.newRelicAgent.addPageAction) {
        this.newRelicAgent.addPageAction('PerformanceEvent', {
          eventType: 'custom_metric',
          metric: metricName,
          duration: value,
          ...enhancedAttributes
        });
      }
      
      // Try newer API methods
      if (this.newRelicAgent && this.newRelicAgent.recordMetric) {
        this.newRelicAgent.recordMetric(metricName, value);
      }

      // Send as custom attribute
      if (this.newRelicAgent && this.newRelicAgent.setCustomAttribute) {
        this.newRelicAgent.setCustomAttribute(`metric_${metricName}`, value);
      }

      console.log(`Custom metric sent to New Relic: ${metricName} = ${value}`, enhancedAttributes);
    } catch (error) {
      console.warn(`Failed to send custom metric ${metricName}:`, error);
    }
  }

  /**
   * Track platform load time (PTLT)
   */
  trackPlatformLoad() {
    this.mark('platform:load:start');
    
    // Return a function to call when platform load is complete
    return () => {
      this.mark('platform:load:end');
      this.measure('Platform Total Load Time (PTLT)', 'platform:load:start', 'platform:load:end');
    };
  }

  /**
   * Track component load time (CTLT)
   * @param {string} componentName - Name of the component
   */
  trackComponentLoad(componentName) {
    const processName = `component:${componentName.toLowerCase()}:load`;
    this.mark(`${processName}:start`);
    
    // Return a function to call when component load is complete
    return () => {
      this.mark(`${processName}:end`);
      this.measure(`Component Load Time - ${componentName} (CTLT)`, `${processName}:start`, `${processName}:end`);
    };
  }

  /**
   * Track route change duration
   * @param {string} routeName - Name of the route
   */
  trackRouteChange(routeName) {
    this.mark('route:change:start');
    console.log(`Tracking route change to: ${routeName}`);
    
    // Return a function to call when route change is complete
    return () => {
      this.mark('route:change:end');
      this.measure('Route Change Duration', 'route:change:start', 'route:change:end');
      
      // Send route change event to New Relic
      this.sendCustomMetric('RouteChange', routeName);
    };
  }

  /**
   * Track store action duration
   * @param {string} actionName - Name of the store action
   */
  trackStoreAction(actionName) {
    const processName = `store:action:${actionName}`;
    this.mark(`${processName}:start`);
    
    // Return a function to call when action is complete
    return () => {
      this.mark(`${processName}:end`);
      this.measure(`Store Action - ${actionName} Duration`, `${processName}:start`, `${processName}:end`);
    };
  }

  /**
   * Track micro-frontend load time with detailed metrics
   * @param {string} microfrontendName - Name of the micro-frontend
   * @param {Object} metadata - Additional metadata about the micro-frontend
   */
  trackMicrofrontendLoad(microfrontendName, metadata = {}) {
    const processName = `microfrontend:${microfrontendName.toLowerCase()}:load`;
    this.mark(`${processName}:start`);
    
    return (success = true, error = null) => {
      this.mark(`${processName}:end`);
      const duration = this.measure(`Micro-frontend Load Time - ${microfrontendName}`, `${processName}:start`, `${processName}:end`);
      
      // Send detailed micro-frontend metrics
      this.sendCustomMetric('MicrofrontendLoad', duration, {
        microfrontendName: microfrontendName,
        success: success,
        error: error?.message || null,
        ...metadata
      });

      // Send component-level metrics
      this.sendCustomMetric('ComponentLoad', duration, {
        componentType: 'microfrontend',
        componentName: microfrontendName,
        loadSuccess: success,
        ...metadata
      });

      return duration;
    };
  }

  /**
   * Track API call duration
   * @param {string} apiEndpoint - API endpoint name
   */
  trackApiCall(apiEndpoint) {
    const processName = `api:${apiEndpoint.toLowerCase()}`;
    this.mark(`${processName}:start`);
    
    return () => {
      this.mark(`${processName}:end`);
      this.measure(`API Call - ${apiEndpoint} Duration`, `${processName}:start`, `${processName}:end`);
    };
  }

  /**
   * Track user interactions with components
   * @param {string} componentName - Name of the component
   * @param {string} action - User action (click, hover, etc.)
   * @param {Object} metadata - Additional metadata
   */
  trackUserInteraction(componentName, action, metadata = {}) {
    this.sendCustomMetric('UserInteraction', Date.now(), {
      componentName: componentName,
      action: action,
      interactionType: 'user_action',
      ...metadata
    });
  }

  /**
   * Track component render performance
   * @param {string} componentName - Name of the component
   * @param {number} renderTime - Time taken to render
   * @param {Object} metadata - Additional metadata
   */
  trackComponentRender(componentName, renderTime, metadata = {}) {
    this.sendCustomMetric('ComponentRender', renderTime, {
      componentName: componentName,
      renderType: 'component_render',
      ...metadata
    });
  }

  /**
   * Track memory usage
   */
  trackMemoryUsage() {
    if (performance.memory) {
      const memoryInfo = {
        used: performance.memory.usedJSHeapSize,
        total: performance.memory.totalJSHeapSize,
        limit: performance.memory.jsHeapSizeLimit,
        usagePercentage: (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100
      };

      this.sendCustomMetric('MemoryUsage', memoryInfo.usagePercentage, {
        memoryUsed: memoryInfo.used,
        memoryTotal: memoryInfo.total,
        memoryLimit: memoryInfo.limit,
        memoryType: 'javascript_heap'
      });
    }
  }

  /**
   * Track network performance
   * @param {string} resourceUrl - URL of the resource
   * @param {number} loadTime - Time taken to load
   * @param {string} resourceType - Type of resource (script, stylesheet, etc.)
   */
  trackResourceLoad(resourceUrl, loadTime, resourceType = 'unknown') {
    this.sendCustomMetric('ResourceLoad', loadTime, {
      resourceUrl: resourceUrl,
      resourceType: resourceType,
      loadType: 'network_resource'
    });
  }

  /**
   * Track application state changes
   * @param {string} stateName - Name of the state
   * @param {string} action - Action that caused the state change
   * @param {Object} metadata - Additional metadata
   */
  trackStateChange(stateName, action, metadata = {}) {
    this.sendCustomMetric('StateChange', Date.now(), {
      stateName: stateName,
      action: action,
      changeType: 'application_state',
      ...metadata
    });
  }

  /**
   * Start comprehensive monitoring
   */
  startComprehensiveMonitoring() {
    // Track memory usage every 30 seconds
    setInterval(() => {
      this.trackMemoryUsage();
    }, 30000);

    // Track resource loading
    if ('PerformanceObserver' in window) {
      const resourceObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          this.trackResourceLoad(entry.name, entry.duration, entry.initiatorType);
        });
      });
      resourceObserver.observe({ entryTypes: ['resource'] });
    }

    // Track long tasks
    if ('PerformanceObserver' in window) {
      const longTaskObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          this.sendCustomMetric('LongTask', entry.duration, {
            taskType: 'long_task',
            startTime: entry.startTime,
            taskName: entry.name || 'unknown'
          });
        });
      });
      longTaskObserver.observe({ entryTypes: ['longtask'] });
    }

    console.log('Comprehensive performance monitoring started');
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

export default performanceMonitor;
